import React from "react";

const CHUNK = {
  AUTH: "module-auth",
  BOOKINGS: "module-bookings",
  CONTRACTS: "module-contracts",
  PROPERTIES: "module-properties",
  TENANTS: "module-tenants",
  MAINTENANCE: "module-maintenance",
  CONFIG: "module-config",
  DASHBOARD: "module-dashboard",
} as const;

function lazyImportWithRetry<T extends React.ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
  retries = 2,
  delayMs = 1000
): Promise<{ default: T }> {
  return factory().catch((error) => {
    if (retries <= 0) {
      throw error;
    }
    return new Promise<{ default: T }>((resolve) => {
      setTimeout(() => resolve(lazyImportWithRetry(factory, retries - 1, delayMs)), delayMs);
    });
  });
}

function lazyRoute<T extends React.ComponentType<any>>(
  factory: () => Promise<{ default: T }>
): React.LazyExoticComponent<T> {
  return React.lazy(() => lazyImportWithRetry(factory));
}

export const Lazy = {
  // Auth & onboarding
  Login: lazyRoute(() => import(/* webpackChunkName: "module-auth" */ "../app/pages/Login")),
  SignUp: lazyRoute(() => import(/* webpackChunkName: "module-auth" */ "../app/modules/SignUp")),
  ForgotPassword: lazyRoute(() =>
    import(/* webpackChunkName: "module-auth" */ "../app/modules/Login/ForgotPassword")
  ),
  ChangePassword: lazyRoute(() =>
    import(/* webpackChunkName: "module-auth" */ "../app/modules/Login/ChangePassword")
  ),
  Success: lazyRoute(() => import(/* webpackChunkName: "module-auth" */ "../app/modules/Login/Success")),
  SignUpNew: lazyRoute(() => import(/* webpackChunkName: "module-auth" */ "../app/modules/SignUpNew")),
  OTP: lazyRoute(() => import(/* webpackChunkName: "module-auth" */ "../app/modules/SignUpNew/OTP")),
  CreatePassword: lazyRoute(() =>
    import(/* webpackChunkName: "module-auth" */ "../app/modules/SignUpNew/CreatePassword")
  ),
  ShowNotice: lazyRoute(() => import(/* webpackChunkName: "module-auth" */ "../app/pages/ShowNotice")),
  Unauthorized: lazyRoute(() => import(/* webpackChunkName: "module-auth" */ "../app/modules/Unauthorize")),
  PaymentProcess: lazyRoute(() =>
    import(/* webpackChunkName: "module-payment" */ "../app/modules/PaymentProcess")
  ),
  NotFound: lazyRoute(() => import(/* webpackChunkName: "module-public" */ "../app/modules/NotFound")),

  // Properties & leads
  PropertyList: lazyRoute(() =>
    import(/* webpackChunkName: "module-properties" */ "../app/modules/Properties/PropertyList")
  ),
  CreateProperty: lazyRoute(() =>
    import(/* webpackChunkName: "module-properties" */ "../app/modules/Properties/AddProperty/CreateProperty")
  ),
  OfferByTenant: lazyRoute(() =>
    import(/* webpackChunkName: "module-properties" */ "../app/modules/Properties/OfferByTenant")
  ),
  PropertyDetail: lazyRoute(() =>
    import(/* webpackChunkName: "module-properties" */ "../app/modules/Properties/PropertyDetail")
  ),
  PropertyReview: lazyRoute(() =>
    import(/* webpackChunkName: "module-properties" */ "../app/modules/Reviews/PropertyReview")
  ),
  VisitScheduling: lazyRoute(() =>
    import(/* webpackChunkName: "module-properties" */ "../app/modules/Properties/VisitScheduling")
  ),
  PropertiesOnMap: lazyRoute(() =>
    import(/* webpackChunkName: "module-properties" */ "../app/modules/Properties/PropertiesOnMap")
  ),
  UnitDetail: lazyRoute(() =>
    import(/* webpackChunkName: "module-properties" */ "../app/modules/Properties/PropertyDetail/UnitDetail")
  ),
  Leads: lazyRoute(() =>
    import(/* webpackChunkName: "module-properties" */ "../app/modules/LeadManagement/Leads/Leads")
  ),
  LeadsDetail: lazyRoute(() =>
    import(/* webpackChunkName: "module-properties" */ "../app/modules/LeadManagement/LeadsDetail/leadsDetail")
  ),
  OpportuntiyList: lazyRoute(() =>
    import(/* webpackChunkName: "module-properties" */ "../app/modules/LeadManagement/OpportunityList")
  ),
  OpportunityDetail: lazyRoute(() =>
    import(/* webpackChunkName: "module-properties" */ "../app/modules/LeadManagement/OpportunityDetail")
  ),
  Reviews: lazyRoute(() => import(/* webpackChunkName: "module-properties" */ "../app/modules/Reviews")),
  Units: lazyRoute(() => import(/* webpackChunkName: "module-properties" */ "../app/modules/Units")),

  // Tenants & users
  Tenants: lazyRoute(() => import(/* webpackChunkName: "module-tenants" */ "../app/modules/Tenants")),
  AddTenant: lazyRoute(() => import(/* webpackChunkName: "module-tenants" */ "../app/modules/Tenants/AddTenant")),
  TenantDetails: lazyRoute(() =>
    import(/* webpackChunkName: "module-tenants" */ "../app/modules/Tenants/TenantDetails")
  ),
  AllProspects: lazyRoute(() =>
    import(/* webpackChunkName: "module-tenants" */ "../app/modules/Tenants/AllProspects")
  ),
  Managers: lazyRoute(() => import(/* webpackChunkName: "module-tenants" */ "../app/modules/Managers")),
  Owner: lazyRoute(() => import(/* webpackChunkName: "module-tenants" */ "../app/modules/Owner")),
  ContractTenantDetails: lazyRoute(() =>
    import(/* webpackChunkName: "module-tenants" */ "../app/modules/ContractTenantDetails")
  ),

  // Bookings
  BookingListing: lazyRoute(() =>
    import(/* webpackChunkName: "module-bookings" */ "../app/modules/BookingListing")
  ),
  BookingRenewalListing: lazyRoute(() =>
    import(/* webpackChunkName: "module-bookings" */ "../app/modules/BookingRenewalListing")
  ),
  BookingformList: lazyRoute(() =>
    import(/* webpackChunkName: "module-bookings" */ "../app/modules/BookingformList")
  ),
  BookingForm: lazyRoute(() =>
    import(/* webpackChunkName: "module-bookings" */ "../app/modules/LeasingFormNew/BookingForm")
  ),
  CommercialRenewalTerms: lazyRoute(() =>
    import(/* webpackChunkName: "module-bookings" */ "../app/modules/Renewal")
  ),
  RenewalForm: lazyRoute(() =>
    import(/* webpackChunkName: "module-bookings" */ "../app/modules/LeasingFormNew/RenewalForm")
  ),
  NewBookingForm: lazyRoute(() =>
    import(/* webpackChunkName: "module-bookings" */ "../app/modules/BookingForm/BookingForm")
  ),
  ViewBookingForm: lazyRoute(() =>
    import(/* webpackChunkName: "module-bookings" */ "../app/modules/BookingForm/ViewBookingForm")
  ),
  ContractBookingForm: lazyRoute(() =>
    import(/* webpackChunkName: "module-bookings" */ "../app/modules/ContractFlow/ContractBookingForm")
  ),

  // Contracts & finance
  ContractsList: lazyRoute(() => import(/* webpackChunkName: "module-contracts" */ "../app/modules/Contracts")),
  ContractDetail: lazyRoute(() =>
    import(/* webpackChunkName: "module-contracts" */ "../app/modules/Contracts/ContractDetail")
  ),
  PostDatedCheques: lazyRoute(() =>
    import(/* webpackChunkName: "module-contracts" */ "../app/modules/Contracts/PostDatedCheques")
  ),
  Receipts: lazyRoute(() =>
    import(/* webpackChunkName: "module-contracts" */ "../app/modules/Contracts/Receipts")
  ),
  EjariList: lazyRoute(() => import(/* webpackChunkName: "module-contracts" */ "../app/modules/Ejari")),
  MoveOut: lazyRoute(() => import(/* webpackChunkName: "module-contracts" */ "../app/modules/MoveOut")),
  ChequesApproval: lazyRoute(() =>
    import(/* webpackChunkName: "module-contracts" */ "../app/modules/ChequesApproval")
  ),
  TerminationProcess: lazyRoute(() =>
    import(/* webpackChunkName: "module-contracts" */ "../app/modules/TerminationProcess/TerminationProcess")
  ),

  // Maintenance
  Maintenance: lazyRoute(() =>
    import(/* webpackChunkName: "module-maintenance" */ "../app/modules/Maintenance")
  ),
  Tickets: lazyRoute(() =>
    import(/* webpackChunkName: "module-maintenance" */ "../app/modules/Maintenance/Tickets/Tickets")
  ),
  Technicians: lazyRoute(() =>
    import(/* webpackChunkName: "module-maintenance" */ "../app/modules/Maintenance/Technicians/Technicians")
  ),
  Roster: lazyRoute(() =>
    import(/* webpackChunkName: "module-maintenance" */ "../app/modules/Maintenance/Roster/Roster")
  ),
  Zones: lazyRoute(() =>
    import(/* webpackChunkName: "module-maintenance" */ "../app/modules/Maintenance/Zones/Zones")
  ),
  ServiceProvider: lazyRoute(() =>
    import(/* webpackChunkName: "module-maintenance" */ "../app/modules/ServiceProvider")
  ),
  ServiceRequests: lazyRoute(() =>
    import(/* webpackChunkName: "module-maintenance" */ "../app/pages/Maintenance/ServiceRequests")
  ),

  // Dashboards
  BookingDashboard: lazyRoute(() =>
    import(/* webpackChunkName: "module-dashboard" */ "../app/pages/BookingDashboard")
  ),
  MaintenanceDashboard: lazyRoute(() =>
    import(/* webpackChunkName: "module-dashboard" */ "../app/pages/Maintenance")
  ),
  RenewalDashboard: lazyRoute(() =>
    import(/* webpackChunkName: "module-dashboard" */ "../app/pages/RenewalDashboard")
  ),

  // Config & misc
  Configurations: lazyRoute(() =>
    import(/* webpackChunkName: "module-config" */ "../app/modules/Configurations")
  ),
  Shifts: lazyRoute(() =>
    import(/* webpackChunkName: "module-config" */ "../app/modules/Configurations/Shifts/Shifts")
  ),
  Categories: lazyRoute(() =>
    import(/* webpackChunkName: "module-config" */ "../app/modules/Configurations/Categories/Categories")
  ),
  PublicHolidays: lazyRoute(() =>
    import(/* webpackChunkName: "module-config" */ "../app/modules/Configurations/PublicHolidays/PublicHolidays")
  ),
  Notices: lazyRoute(() => import(/* webpackChunkName: "module-config" */ "../app/modules/Notices")),
  Notification: lazyRoute(() =>
    import(/* webpackChunkName: "module-config" */ "../app/modules/Notification")
  ),
  NotificationCalendar: lazyRoute(() =>
    import(/* webpackChunkName: "module-config" */ "../app/modules/Notification/NotificationCalendar")
  ),
  ContactUs: lazyRoute(() => import(/* webpackChunkName: "module-config" */ "../app/modules/Contact Us")),
};

export { CHUNK };
